import React, { useState, useCallback, useEffect } from 'react';
import { Upload, AlertTriangle, X } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { photoSafetyService, SchoolContentResult } from '@/services/photoSafetyService';

interface PhotoWithStatus {
  id: string;
  file: File;
  preview: string;
  uploadStatus: 'pending' | 'uploading' | 'scanning' | 'complete' | 'error' | 'safe' | 'warning' | 'blocked';
  schoolContent?: SchoolContentResult;
  scanResult?: {
    flaggedLabels: string[];
  };
  uploadUrl?: string;
  key?: string;
}

interface PhotoUploadProps {
  onPhotosUploaded?: (photos: { id: string; key: string; status: string }[]) => void;
  maxPhotos?: number;
  initialPhotos?: { url: string; order: number; scanStatus: 'pending' | 'safe' | 'warning' | 'blocked' }[];
  showSafetyBadges?: boolean; // Control whether to show safety badges on uploaded photos
}

export const PhotoUpload: React.FC<PhotoUploadProps> = ({
  onPhotosUploaded: _onPhotosUploaded,
  maxPhotos = 5,
  initialPhotos = [],
  showSafetyBadges = true, // Default to true for upload flow
}) => {
  const { getToken } = useAuth();
  const [photos, setPhotos] = useState<PhotoWithStatus[]>([]);

  // Initialize with existing photos
  useEffect(() => {
    if (initialPhotos.length > 0 && photos.length === 0) {
      const existingPhotos: PhotoWithStatus[] = initialPhotos.map((p) => {
        // Extract key from URL - handle both localhost and production URLs
        // Format: http://domain/api/photos/serve/photos/userId/timestamp-uuid.ext
        const urlParts = p.url.split('/api/photos/serve/');
        const key = urlParts.length > 1 ? urlParts[1] : p.url;

        return {
          id: crypto.randomUUID(),
          file: new File([], 'existing'), // Dummy file for existing photos
          preview: p.url,
          uploadStatus: p.scanStatus,
          key,
          uploadUrl: p.url,
        };
      });
      setPhotos(existingPhotos);
    }
  }, [initialPhotos]);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoWithStatus | null>(null);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Initialize photo safety service on mount
  useEffect(() => {
    photoSafetyService.preloadModels();
  }, []);

  // Mutation for getting presigned URL
  const getPresignedUrl = useMutation({
    mutationFn: async ({ fileName, fileType }: { fileName: string; fileType: string }) => {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/photos/presigned-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileName, fileType }),
      });
      if (!response.ok) throw new Error('Failed to get upload URL');
      return response.json();
    },
  });

  // Removed unused polling function - we're using client-side scanning

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newPhotos: PhotoWithStatus[] = [];
    const remainingSlots = maxPhotos - photos.length;

    for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
      const file = files[i];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }

      const preview = URL.createObjectURL(file);
      newPhotos.push({
        id: crypto.randomUUID(),
        file,
        preview,
        uploadStatus: 'pending',
      });
    }

    if (newPhotos.length > 0) {
      setPhotos(prev => [...prev, ...newPhotos]);

      // Process each photo
      for (const photo of newPhotos) {
        await processPhoto(photo);
      }
    }
  }, [photos.length, maxPhotos]);

  const processPhoto = async (photo: PhotoWithStatus) => {
    setIsProcessing(true);

    try {
      // Update status to scanning
      setPhotos(prev => prev.map(p =>
        p.id === photo.id ? { ...p, uploadStatus: 'scanning' } : p
      ));

      // Add minimum delay to ensure animation is visible
      const [safetyResult] = await Promise.all([
        photoSafetyService.checkPhotoSafety(photo.file),
        new Promise(resolve => setTimeout(resolve, 2000)) // Minimum 2 seconds for animation
      ]);

      console.log('🔍 [PhotoUpload] Safety scan complete:', {
        photoId: photo.id,
        safetyResult: safetyResult,
        schoolContent: safetyResult.schoolContentDetected,
      });

      // Update photo with school content results - determine scan status immediately
      // Default to safe if detection failed
      const schoolContent = safetyResult?.schoolContentDetected || {
        classroom: false,
        school: false,
        students: false,
        other: false
      };

      console.log('🔍 [After Scan] Saving schoolContent:', {
        photoId: photo.id,
        schoolContent: schoolContent
      });

      // Generate flagged labels for the UI
      const flaggedLabels: string[] = [];
      if (schoolContent.classroom) flaggedLabels.push('Classroom');
      if (schoolContent.school) flaggedLabels.push('School');
      if (schoolContent.students) flaggedLabels.push('Students');
      if (schoolContent.other) flaggedLabels.push('Other');

      // Determine scan result status immediately after detection
      const hasDetections = schoolContent.classroom || schoolContent.school || schoolContent.students || schoolContent.other;
      const scanStatus: 'safe' | 'warning' = hasDetections ? 'warning' : 'safe';

      // Update to show scan result with detected labels BEFORE upload starts
      setPhotos(prev => prev.map(p =>
        p.id === photo.id
          ? {
            ...p,
            schoolContent: schoolContent,
            scanResult: { flaggedLabels },
            uploadStatus: scanStatus, // Set to final status immediately
          }
          : p
      ));

      // No delay - badge stays visible until user saves profile

      const { uploadUrl, key, photoId } = await getPresignedUrl.mutateAsync({
        fileName: photo.file.name,
        fileType: photo.file.type,
      });

      // Step 3: Upload to R2 using FormData
      const formData = new FormData();
      formData.append('file', photo.file);
      formData.append('key', key);
      formData.append('photoId', photoId);

      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${await getToken()}`,
        },
      });

      if (!uploadResponse.ok) {
        const error = await uploadResponse.text();
        console.error('Upload failed:', error);
        throw new Error('Failed to upload photo');
      }

      // Update photo with key after successful upload
      setPhotos(prev => {
        const updated = prev.map(p =>
          p.id === photo.id ? { ...p, key, uploadUrl } : p
        );
        console.log('📸 [PhotoUpload] Updated photo with key:', { photoId: photo.id, key, uploadUrl });
        return updated;
      });

      // Step 4: Save scan result to backend
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
        const hasDetections = schoolContent.classroom || schoolContent.school || schoolContent.students || schoolContent.other;
        const scanStatus = hasDetections ? 'warning' : 'safe';

        const response = await fetch(`${apiUrl}/api/photos/scan-result`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            photoId,
            scanStatus,
            flaggedLabels: [
              ...(schoolContent.classroom ? ['Classroom'] : []),
              ...(schoolContent.school ? ['School'] : []),
              ...(schoolContent.students ? ['Students'] : []),
              ...(schoolContent.other ? ['Other'] : [])
            ],
            scanDetails: {
              schoolContent: {
                classroom: schoolContent.classroom,
                school: schoolContent.school,
                students: schoolContent.students,
                other: schoolContent.other,
                identifiableLocation: false,
                textContent: []
              },
              modelUsed: safetyResult.modelUsed,
              processingTimeMs: safetyResult.processingTimeMs
            }
          }),
        });

        if (response.ok) {
          console.log('✅ [PhotoUpload] Scan result saved to database');
        } else {
          const errorText = await response.text();
          console.error('❌ [PhotoUpload] Failed to save scan result (HTTP', response.status, ')');
          console.error('API Error Response:', errorText);
          try {
            const errorJson = JSON.parse(errorText);
            console.error('Validation errors:', JSON.stringify(errorJson, null, 2));
          } catch (e) {
            console.error('Error response was not JSON:', errorText);
          }
        }
      } catch (error) {
        console.error('❌ [PhotoUpload] Error saving scan result:', error);
      }

      // Upload complete - photo status was already set to 'safe'/'warning' after scan
      console.log('📸 [Upload Complete] Photo uploaded successfully:', {
        photoId: photo.id,
        key,
        uploadUrl,
        schoolContent
      });

    } catch (error) {
      console.error('Photo processing error:', error);
      setPhotos(prev => prev.map(p =>
        p.id === photo.id ? { ...p, uploadStatus: 'error' } : p
      ));
      toast.error('Failed to process photo. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemovePhoto = (photoId: string) => {
    setPhotos(prev => {
      const photo = prev.find(p => p.id === photoId);
      if (photo) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter(p => p.id !== photoId);
    });
  };

  const handleReplacePhoto = () => {
    if (selectedPhoto) {
      handleRemovePhoto(selectedPhoto.id);
    }
    setWarningDialogOpen(false);
    setSelectedPhoto(null);
  };

  const handleKeepPhoto = () => {
    if (selectedPhoto) {
      setPhotos(prev => prev.map(p =>
        p.id === selectedPhoto.id ? { ...p, uploadStatus: 'complete' } : p
      ));
    }
    setWarningDialogOpen(false);
    setSelectedPhoto(null);
  };

  // Notify parent component of photo changes
  useEffect(() => {
    if (_onPhotosUploaded) {
      console.log('📷 PhotoUpload: All photos state:', photos);
      // Filter for photos that have been uploaded and processed (safe or warning status)
      const uploadedPhotos = photos
        .filter(p => {
          const hasKey = !!p.key;
          const hasStatus = p.uploadStatus === 'safe' || p.uploadStatus === 'warning';
          console.log(`📷 Photo ${p.id}: hasKey=${hasKey}, status=${p.uploadStatus}, hasStatus=${hasStatus}`);
          return hasKey && hasStatus;
        })
        .map(p => ({
          id: p.id,
          key: p.key || '',
          status: p.uploadStatus,
        }));
      console.log('📷 PhotoUpload: Filtered photos to send to parent:', uploadedPhotos);
      _onPhotosUploaded(uploadedPhotos);
    }
  }, [photos]);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      photos.forEach(photo => {
        URL.revokeObjectURL(photo.preview);
      });
    };
  }, []);

  // Debug logging for render
  console.log('🎨 [PhotoUpload] Rendering photos:', photos.map(p => ({
    id: p.id,
    status: p.uploadStatus,
    hasLabels: !!p.scanResult?.flaggedLabels,
    labelCount: p.scanResult?.flaggedLabels?.length || 0,
    labels: p.scanResult?.flaggedLabels
  })));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {photos.map(photo => (
          <Card key={photo.id} className="relative overflow-hidden aspect-square bg-brand-surface border-brand-border rounded-2xl">
            <img
              src={photo.preview}
              alt="Upload preview"
              className={`w-full h-full object-cover ${photo.uploadStatus === 'warning' ? 'ring-2 ring-yellow-500' : ''
                }`}
            />

            {/* Status overlays */}
            {(photo.uploadStatus === 'scanning' || photo.uploadStatus === 'uploading') && (
              <div className="absolute inset-0 bg-gradient-to-b from-brand-background/95 via-brand-background/90 to-brand-background/95">
                {/* Animated scan line and fill */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-0 left-0 right-0 animate-scan-line border-b-[2px] border-[#ff00ff] bg-gradient-to-t from-[#ff00ff]/20 to-transparent z-10 shadow-[0_4px_20px_rgba(255,0,255,0.5)]">
                    {/* Glowing line effect at the bottom */}
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#ff00ff] shadow-[0_0_10px_#ff00ff,0_0_20px_#ff00ff,0_0_30px_#ff00ff]" />
                  </div>
                </div>

                {/* Corner brackets for sci-fi effect */}
                <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-brand-primary" />
                <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-brand-primary" />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-brand-primary" />
                <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-brand-primary" />

                {/* Center status */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center space-y-2">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse" />
                      <span className="text-sm font-mono text-brand-primary animate-pulse">ANALYZING</span>
                      <div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse" />
                    </div>
                  </div>
                </div>

                {/* Detected labels - shown at bottom */}
                {photo.scanResult?.flaggedLabels && photo.scanResult.flaggedLabels.length > 0 && (
                  <div className="absolute bottom-12 left-0 right-0 px-4">
                    <div className="bg-brand-background/95 backdrop-blur-sm rounded-lg p-2 border border-brand-primary/30">
                      <div className="text-xs font-mono text-brand-primary mb-1">DETECTED:</div>
                      <div className="flex flex-wrap gap-1">
                        {photo.scanResult.flaggedLabels.map((label: string, idx: number) => (
                          <span
                            key={idx}
                            className="text-xs bg-brand-primary/20 text-brand-primary px-2 py-0.5 rounded border border-brand-primary/40 animate-fade-in"
                            style={{ animationDelay: `${idx * 100}ms` }}
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}



            {/* Safety status overlay - Show during upload flow only if showSafetyBadges is true */}
            {showSafetyBadges && (photo.uploadStatus === 'safe' || photo.uploadStatus === 'warning') && (() => {
              // Default to safe if no schoolContent or detection failed
              const schoolContent = photo.schoolContent || { classroom: false, school: false, students: false, other: false };
              const hasDetections = schoolContent.classroom || schoolContent.school || schoolContent.students || schoolContent.other;
              return (
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="bg-brand-background/95 backdrop-blur-sm rounded-lg p-2 border border-emerald-500/30">
                    <div className="flex items-center gap-2 text-xs font-mono text-emerald-400 mb-1">
                      {hasDetections ? (
                        <>
                          <span className="text-yellow-400">⚠️</span>
                          <span>DETECTED:</span>
                        </>
                      ) : (
                        <>
                          <span className="text-emerald-400">✓</span>
                          <span>SAFE</span>
                        </>
                      )}
                    </div>
                    {hasDetections && (
                      <div className="flex flex-col gap-0.5 text-xs">
                        {schoolContent.classroom && (
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-400">•</span>
                            <span className="text-yellow-400">Classroom</span>
                          </div>
                        )}
                        {schoolContent.school && (
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-400">•</span>
                            <span className="text-yellow-400">School</span>
                          </div>
                        )}
                        {schoolContent.students && (
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-400">•</span>
                            <span className="text-yellow-400">Students</span>
                          </div>
                        )}
                        {schoolContent.other && (
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-400">•</span>
                            <span className="text-yellow-400">Other</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}



            {/* Remove button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 left-2 w-8 h-8 bg-brand-surface/80 hover:bg-brand-surface backdrop-blur-sm"
              onClick={() => handleRemovePhoto(photo.id)}
            >
              <X className="w-4 h-4 text-brand-primary" />
            </Button>
          </Card>
        ))}

        {/* Add photo button */}
        {photos.length < maxPhotos && (
          <Card className="relative overflow-hidden aspect-square bg-brand-surface border-2 border-dashed border-brand-border cursor-pointer hover:border-brand-primary hover:shadow-[0_0_30px_rgba(178,251,165,0.3)] transition-all duration-300 group rounded-2xl">
            <label className="flex items-center justify-center w-full h-full cursor-pointer">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                disabled={isProcessing}
              />
              <div className="text-center space-y-4 p-8">
                <div className="relative">
                  <Upload className="w-16 h-16 mx-auto text-brand-muted group-hover:text-brand-primary transition-all duration-300 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-brand-primary/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="space-y-1">
                  <p className="text-lg font-semibold text-brand-text group-hover:text-brand-primary transition-colors">Add Photo</p>
                  <p className="text-xs text-brand-muted">Click to upload or drag & drop</p>
                  <p className="text-xs text-brand-muted/60">Max 10MB • JPG, PNG</p>
                </div>
              </div>
            </label>
          </Card>
        )}
      </div>

      {/* Warning Dialog */}
      <Dialog open={warningDialogOpen} onOpenChange={setWarningDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Privacy Warning
            </DialogTitle>
            <DialogDescription>
              This photo may reveal your school or workplace. We detected:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {selectedPhoto?.scanResult?.flaggedLabels.map((label: string, index: number) => (
              <div key={index} className="flex items-center gap-2">
                <span className="text-sm bg-yellow-500/20 text-yellow-400 px-3 py-1.5 rounded-lg border border-yellow-500/30">
                  {label}
                </span>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleReplacePhoto}
              className="bg-brand-surface border-brand-border hover:bg-brand-border"
            >
              Replace Photo
            </Button>
            <Button
              onClick={handleKeepPhoto}
              className="bg-brand-primary hover:bg-brand-primary/90 text-brand-surface"
            >
              Keep Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};